import React from 'react';
import {
    CampaignOutlinedIcon,
    CodeIcon,
    DashboardOutlinedIcon,
    ImageOutlinedIcon,
    MediationOutlinedIcon,
    PersonOutlineOutlinedIcon,
    TrackChangesOutlinedIcon,
    FlightLandOutlinedIcon,
    BarChartIcon,
} from 'tuui';
import { CookieConsent } from 'components/CookieConsent/CookieConsent';
import { MODULE_NAME as THIRD_PARTY_CONNECT_MODULE } from 'modules/3p-connect';
import {
    externalPath as accountManagementExternalPath,
    hrefGetter as accountManagementHrefGetter,
    isAdminNetworkAccountType,
    isGroupAccountType,
    isLowTouchAccount,
    isNetworkAccountType,
    isPartnerAccountType,
    isVideoAccount,
    MODULE_NAME as ACCOUNT_MGMT_MODULE,
    permissions as ACCOUNT_MGMT_PERMISSIONS,
} from 'modules/account-management';
import { MODULE_NAME as ACCOUNTS_MODULE } from 'modules/accounts';
import { MODULE_NAME as ADVANCED_PREVIEW_MODULE } from 'modules/advanced-preview';
import { MODULE_NAME as AUDIENCE_INSIGHTS_MODULE } from 'modules/audience-insights';
import { AudiencesNavigationItemContent } from 'modules/audiences/components/NavigationItemContent/AudiencesNavigationItemContent';
import { MODULE_NAME as AUDIENCES_MODULE } from 'modules/audiences/config/routes/module';
import {
    MODULE_NAME as BILLING_AND_PAYMENTS_MODULE,
    visibilityCondition as billingAndPaymentVisibilityCondition,
} from 'modules/billing-and-payments';
import {
    MODULE_NAME as CAMPAIGN_BENCHMARKING_MODULE,
    permissions as CAMPAIGN_BENCHMARKING_PERMISSIONS,
} from 'modules/campaign-benchmarking';
import {
    externalPath as campaignReviewExternalPath,
    hrefGetter as campaignReviewHrefGetter,
    MODULE_NAME as CAMPAIGN_REVIEW_MODULE,
    permissions as CAMPAIGN_REVIEW_PERMISSIONS,
} from 'modules/campaign-review';
import { config as campaignsConfig } from 'modules/campaigns';
import { MODULE_NAME as CAMPAIGNS_MODULE } from 'modules/campaigns/config';
import { MODULE_NAME as CODELESS_MODULE } from 'modules/codeless-events/config/module';
import {
    externalPath as contactSupportExternalPath,
    hrefGetter as contactSupportHrefGetter,
    MODULE_NAME as CONTACT_SUPPORT_MODULE,
    permissions as CONTACT_SUPPORT_PERMISSIONS,
} from 'modules/contact-support';
import { MODULE_NAME as CREATIVE_LIBRARY_MODULE } from 'modules/creative-library/config/routes';
import { permissions as homePermissions } from 'modules/home/config/permissions';
import { MODULE_NAME as HOME_MODULE } from 'modules/home/config/routes/module';
import { MODULE_NAME as LANDING_PAGES_MODULE, permissions as landingPagesPermissions } from 'modules/landing-pages';
import {
    externalPath as lightAccountManagementExternalPath,
    hrefGetter as lightAccountManagementHrefGetter,
    MODULE_NAME as LIGHT_ACCOUNT_MGMT_MODULE,
    permissions as LIGHT_ACCOUNT_MGMT_PERMISSIONS,
} from 'modules/light-account-management';
import { MODULE_NAME as ONBOARDING_MODULE } from 'modules/onboarding';
import { MODULE_NAME as PERFORMANCE_RULES_MODULE } from 'modules/performance-rules/config/routes';
import { MODULE_NAME as PUSH_API_MODULE } from 'modules/push-api';
import { MODULE_NAME as SCHEDULED_REPORTS_MODULE } from 'modules/scheduled-reports/config/routes';
import {
    MODULE_NAME as SEARCH_ADD_KEYWORD_MODULE,
    permissions as SEARCH_ADD_KEYWORD_PERMISSIONS,
} from 'modules/search-ads-keyword';
import { FEATURE_FLAGS } from 'modules/taboola-common-frontend-modules/account-configurations';
import {
    BACKSTAGE_CONTENT_PERMISSION,
    FETCH_USER_SUCCESS,
    isInternalUser,
    PERMISSIONS_OPERATORS,
} from 'modules/taboola-common-frontend-modules/authentication';
import { GTM_EVENTS, gtmTracker } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { lazyCustom } from 'modules/taboola-common-frontend-modules/lazy-loading/lazyCustom';
import { resolveLazyComponent } from 'modules/taboola-common-frontend-modules/lazy-loading/resolveLazyComponent';
import { withSuspense } from 'modules/taboola-common-frontend-modules/lazy-loading/withSuspense';
import { MODULE_NAME as TRACKING_MODULE } from 'modules/tracking';
import { TrackingNavigationItemContent } from 'modules/tracking/components/NavigationItemContent/TrackingNavigationItemContent';
import {
    externalPath as userManagementExternalPath,
    MODULE_NAME as USER_MGMT_MODULE,
    hrefGetter as userManagementHrefGetter,
    permissions as USER_MGMT_PERMISSIONS,
} from 'modules/user-management';
import { USER_TYPE } from 'services/constants';
import { AudiencesBadge } from '../components/NavigationSideBar/components/AudiencesBadge/AudiencesBadge';
import { TrackingBadge } from '../components/NavigationSideBar/components/TrackingBadge/TrackingBadge';

const TrackingLazy = lazyCustom(() => import('modules/tracking/pages/Tracking').then(resolveLazyComponent('Tracking')));
const Tracking = withSuspense(TrackingLazy);

const ThirdPartyConnectLazy = lazyCustom(() =>
    import('modules/3p-connect/pages/ThirdPartyConnect').then(resolveLazyComponent('ThirdPartyConnect'))
);
const ThirdPartyConnect = withSuspense(ThirdPartyConnectLazy);

const AdvancedPreviewPageLazy = lazyCustom(() =>
    import('modules/advanced-preview/AdvancedPreviewPage').then(resolveLazyComponent('AdvancedPreviewPage'))
);
const AdvancedPreviewPage = withSuspense(AdvancedPreviewPageLazy);

const AccountsModulePageLazy = lazyCustom(() =>
    import('../modules/accounts/pages/AccountsModulePage').then(resolveLazyComponent('AccountsModulePage'))
);
const AccountsModulePage = withSuspense(AccountsModulePageLazy);

const AudienceInsightsLazy = lazyCustom(() =>
    import('modules/audience-insights/AudienceInsights').then(resolveLazyComponent('AudienceInsights'))
);
const AudienceInsights = withSuspense(AudienceInsightsLazy);

const AudiencesLazy = lazyCustom(() => import('modules/audiences/Audiences').then(resolveLazyComponent('Audiences')));
const Audiences = withSuspense(AudiencesLazy);

const BillingAndPaymentsModulePageLazy = lazyCustom(() =>
    import('modules/billing-and-payments/pages/BillingAndPaymentsModulePage').then(
        resolveLazyComponent('BillingAndPaymentsModulePage')
    )
);
const BillingAndPaymentsModulePage = withSuspense(BillingAndPaymentsModulePageLazy);

const CampaignBenchmarkingComponentLazy = lazyCustom(() =>
    import('modules/campaign-benchmarking/CampaignBenchmarkingComponent').then(
        resolveLazyComponent('CampaignBenchmarkingComponent')
    )
);
const CampaignBenchmarkingComponent = withSuspense(CampaignBenchmarkingComponentLazy);

const CampaignReviewManagementLazy = lazyCustom(() =>
    import('modules/campaign-review/CampaignReviewManagement').then(resolveLazyComponent('CampaignReviewManagement'))
);
const CampaignReviewManagement = withSuspense(CampaignReviewManagementLazy);

const ConversionSetupWrapperLazy = lazyCustom(() =>
    import(
        '../modules/codeless-events/src/main/webapp/components/CodelessEvents/components/ConversionSetupWrapper/ConversionSetupWrapper'
    ).then(resolveLazyComponent('ConversionSetupWrapper'))
);
const ConversionSetupWrapper = withSuspense(ConversionSetupWrapperLazy);

const ContactSupportLazy = lazyCustom(() =>
    import('modules/contact-support/ContactSupport').then(resolveLazyComponent('ContactSupport'))
);
const ContactSupport = withSuspense(ContactSupportLazy);

const LightAccountManagementLazy = lazyCustom(() =>
    import('modules/light-account-management/LightAccountManagement').then(
        resolveLazyComponent('LightAccountManagement')
    )
);
const LightAccountManagement = withSuspense(LightAccountManagementLazy);

const AccountManagementLazy = lazyCustom(() =>
    import('modules/account-management/AccountManagement').then(resolveLazyComponent('AccountManagement'))
);
const AccountManagement = withSuspense(AccountManagementLazy);

const OnboardingLazy = lazyCustom(() =>
    import('modules/onboarding/Onboarding').then(resolveLazyComponent('Onboarding'))
);
const Onboarding = withSuspense(OnboardingLazy);

const PerformanceRulesLazy = lazyCustom(() =>
    import('modules/performance-rules/pages/PerformanceRules').then(resolveLazyComponent('PerformanceRules'))
);
const PerformanceRules = withSuspense(PerformanceRulesLazy);

const PushApiLazy = lazyCustom(() => import('modules/push-api/pages/PushApi').then(resolveLazyComponent('PushApi')));
const PushApi = withSuspense(PushApiLazy);

const ScheduledReportsLazy = lazyCustom(() =>
    import('modules/scheduled-reports/pages/ScheduledReports').then(resolveLazyComponent('ScheduledReports'))
);
const ScheduledReports = withSuspense(ScheduledReportsLazy);

const SearchAdsKeywordComponentLazy = lazyCustom(() =>
    import('modules/search-ads-keyword/SearchAdsKeywordComponent').then(
        resolveLazyComponent('SearchAdsKeywordComponent')
    )
);
const SearchAdsKeywordComponent = withSuspense(SearchAdsKeywordComponentLazy);

const UserManagementLazy = lazyCustom(() =>
    import('modules/user-management').then(resolveLazyComponent('UserManagement'))
);
const UserManagement = withSuspense(UserManagementLazy);

const CampaignsLazy = lazyCustom(() => import('modules/campaigns/Campaigns').then(resolveLazyComponent('Campaigns')));
const Campaigns = withSuspense(CampaignsLazy);

const HomeLazy = lazyCustom(() => import('modules/home/Home').then(resolveLazyComponent('Home')));
const Home = withSuspense(HomeLazy);

const CreativeLibraryLazy = lazyCustom(() =>
    import('modules/creative-library/pages/CreativeLibrary').then(resolveLazyComponent('CreativeLibrary'))
);
const CreativeLibrary = withSuspense(CreativeLibraryLazy);

const LandingPagesLazy = lazyCustom(() =>
    import('modules/landing-pages/pages/LandingPages').then(resolveLazyComponent('LandingPages'))
);
const LandingPages = withSuspense(LandingPagesLazy);

/**
 * Application configuration object containing module routing, external apps, and analytics setup.
 * @type {import('../modules/module.jsdoc').AppConfig}
 */
const config = {
    routingMap: {
        [ADVANCED_PREVIEW_MODULE]: {
            id: ADVANCED_PREVIEW_MODULE,
            component: AdvancedPreviewPage,
            nonAuthAccess: true,
            responsive: true,
            hideNavItem: true,
            hidden: true,
        },
        [HOME_MODULE]: {
            id: HOME_MODULE,
            groupId: HOME_MODULE,
            icon: DashboardOutlinedIcon,
            component: Home,
            isDefault: false,
            caption: {
                id: 'navigation.caption.home',
                default: 'Realize Home',
            },
            componentProps: {
                key: HOME_MODULE,
                id: HOME_MODULE,
            },
            accountConfigurations: { [FEATURE_FLAGS.REALIZE_HOME_ENABLED]: 'true' },
            permissions: homePermissions,
        },
        [CAMPAIGNS_MODULE]: {
            id: CAMPAIGNS_MODULE,
            groupId: CAMPAIGNS_MODULE,
            icon: CampaignOutlinedIcon,
            component: Campaigns,
            isDefault: true,
            permissions: {
                permissions: ['CAMPAIGN_EDIT', 'CAMPAIGN_VIEW', 'TABOOLA_ADS_CAMPAIGN_MODULE_VIEW'],
                operator: PERMISSIONS_OPERATORS.OR,
            },
            caption: {
                id: 'navigation.caption.campaigns',
                default: 'Campaigns',
            },
            componentProps: {
                id: CAMPAIGNS_MODULE,
            },
            visibilityCondition: ({ selectedAccount: { type, partnerTypes }, permissions }) =>
                permissions['ADS_CONSOLE_VIDEO_CAMPAIGNS'] ||
                isNetworkAccountType(type) ||
                !isVideoAccount(partnerTypes),
        },
        [THIRD_PARTY_CONNECT_MODULE]: {
            id: THIRD_PARTY_CONNECT_MODULE,
            groupId: THIRD_PARTY_CONNECT_MODULE,
            component: ThirdPartyConnect,
            hideHeader: true,
            hideNavItem: true,
            hidden: true,
            responsive: true,
            accountConfigurations: { [FEATURE_FLAGS.THIRD_PARTY_CONNECT_ENABLED]: 'true' },
            caption: {
                id: 'navigation.caption.3p-connect',
                default: 'Connect External App',
            },
            componentProps: {
                id: THIRD_PARTY_CONNECT_MODULE,
                key: THIRD_PARTY_CONNECT_MODULE,
            },
        },
        [ACCOUNTS_MODULE]: {
            id: ACCOUNTS_MODULE,
            groupId: ACCOUNTS_MODULE,
            component: AccountsModulePage,
            isDefault: true,
            hideNavItem: true,
            permissions: {
                permissions: ['ACCOUNT_EDIT', 'LIGHT_PUBLISHER_EDIT'],
                operator: PERMISSIONS_OPERATORS.OR,
            },
            caption: {
                id: 'navigation.caption.accounts',
                default: 'Account Management',
            },
            componentProps: {
                key: ACCOUNTS_MODULE,
                id: ACCOUNTS_MODULE,
            },
            accountConfigurations: { [FEATURE_FLAGS.ACCOUNT_MANAGEMENT_ENABLED]: 'true' },
        },
        [CREATIVE_LIBRARY_MODULE]: {
            id: CREATIVE_LIBRARY_MODULE,
            groupId: CREATIVE_LIBRARY_MODULE,
            icon: ImageOutlinedIcon,
            component: CreativeLibrary,
            isDefault: false,
            caption: {
                id: 'navigation.caption.creative.library',
                default: 'Creative Library',
            },
            accountConfigurations: { [FEATURE_FLAGS.CREATIVE_LIBRARY_ENABLED]: 'true' },
            componentProps: {
                key: CREATIVE_LIBRARY_MODULE,
                id: CREATIVE_LIBRARY_MODULE,
            },
        },
        [TRACKING_MODULE]: {
            id: TRACKING_MODULE,
            groupId: TRACKING_MODULE,
            icon: TrackChangesOutlinedIcon,
            component: Tracking,
            isDefault: false,
            navigationItemContent: TrackingNavigationItemContent,
            visibilityCondition: ({ selectedAccount: { type } }) =>
                !(isAdminNetworkAccountType(type) || isGroupAccountType(type)),
            permissions: ['TABOOLA_PIXEL_MODULE_VIEWER'],
            caption: {
                id: 'navigation.caption.tracking',
                default: 'Tracking',
            },
            componentProps: {
                key: TRACKING_MODULE,
                id: TRACKING_MODULE,
            },
            badgeComponent: TrackingBadge,
        },
        [AUDIENCES_MODULE]: {
            id: AUDIENCES_MODULE,
            groupId: AUDIENCES_MODULE,
            icon: PersonOutlineOutlinedIcon,
            component: Audiences,
            isDefault: false,
            navigationItemContent: AudiencesNavigationItemContent,
            visibilityCondition: ({ selectedAccount: { type } }) =>
                !(isAdminNetworkAccountType(type) || isGroupAccountType(type)),
            permissions: ['AUDIENCE_EDIT', BACKSTAGE_CONTENT_PERMISSION],
            operator: PERMISSIONS_OPERATORS.AND,
            caption: {
                id: 'navigation.caption.audiences',
                default: 'Audiences',
            },
            componentProps: {
                key: AUDIENCES_MODULE,
                id: AUDIENCES_MODULE,
            },
            badgeComponent: AudiencesBadge,
        },
        [AUDIENCE_INSIGHTS_MODULE]: {
            id: AUDIENCE_INSIGHTS_MODULE,
            groupId: AUDIENCES_MODULE,
            icon: PersonOutlineOutlinedIcon,
            component: AudienceInsights,
            isDefault: false,
            permissions: ['ADS_CONSOLE_VIEW_AUDIENCE_INSIGHTS'],
            caption: {
                id: 'navigation.caption.audience-insights',
                default: 'Audience Insights',
            },
            componentProps: {
                id: AUDIENCE_INSIGHTS_MODULE,
            },
            visibilityCondition: ({ selectedAccount: { type } }) =>
                !(isAdminNetworkAccountType(type) || isGroupAccountType(type)),
        },
        [PERFORMANCE_RULES_MODULE]: {
            id: PERFORMANCE_RULES_MODULE,
            groupId: PERFORMANCE_RULES_MODULE,
            icon: props => <MediationOutlinedIcon fontSize="small" {...props} />,
            component: PerformanceRules,
            isDefault: false,
            permissions: {
                permissions: ['ADS_CONSOLE_PERFORMANCE_RULES_VIEW'],
            },
            accountConfigurations: { [FEATURE_FLAGS.PERFORMANCE_RULES_ENABLED]: 'true' },
            visibilityCondition: ({ selectedAccount: { type } }) =>
                !(isAdminNetworkAccountType(type) || isGroupAccountType(type)),
            caption: {
                id: 'navigation.caption.performance.rules',
                default: 'Rules',
            },
            componentProps: {
                key: PERFORMANCE_RULES_MODULE,
                id: PERFORMANCE_RULES_MODULE,
            },
        },
        [SCHEDULED_REPORTS_MODULE]: {
            id: SCHEDULED_REPORTS_MODULE,
            groupId: SCHEDULED_REPORTS_MODULE,
            icon: BarChartIcon,
            component: ScheduledReports,
            isDefault: false,
            caption: {
                id: 'navigation.caption.scheduledReports',
                default: 'Reports',
            },
            componentProps: {
                key: SCHEDULED_REPORTS_MODULE,
                id: SCHEDULED_REPORTS_MODULE,
            },
            visibilityCondition: ({ accountConfig, permissionsMap, selectedAccount: { type, partnerTypes } }) =>
                (accountConfig[FEATURE_FLAGS.SCHEDULED_REPORTS_VIEW] === 'true' ||
                    permissionsMap['ADS_CONSOLE_SCHEDULED_REPORTS_VIEW']) &&
                !(
                    isAdminNetworkAccountType(type) ||
                    isGroupAccountType(type) ||
                    (!isNetworkAccountType(type) && isVideoAccount(partnerTypes))
                ),
        },
        [LANDING_PAGES_MODULE]: {
            id: LANDING_PAGES_MODULE,
            groupId: LANDING_PAGES_MODULE,
            icon: FlightLandOutlinedIcon,
            component: LandingPages,
            isDefault: false,
            caption: {
                id: 'navigation.caption.landing.pages',
                default: 'Advertorials',
            },
            componentProps: {
                key: LANDING_PAGES_MODULE,
                id: LANDING_PAGES_MODULE,
            },
            visibilityCondition: ({ selectedAccount: { type } }) => isPartnerAccountType(type),
            accountConfigurations: { [FEATURE_FLAGS.GEN_AI_LANDING_PAGES_MODULE_ENABLED]: 'true' },
            permissions: landingPagesPermissions,
        },
        [LIGHT_ACCOUNT_MGMT_MODULE]: {
            id: LIGHT_ACCOUNT_MGMT_MODULE,
            groupId: LIGHT_ACCOUNT_MGMT_MODULE,
            hideNavItem: true,
            component: LightAccountManagement,
            href: lightAccountManagementHrefGetter,
            legacy: true,
            isDefault: false,
            permissions: LIGHT_ACCOUNT_MGMT_PERMISSIONS,
            componentProps: {
                key: LIGHT_ACCOUNT_MGMT_MODULE,
                id: LIGHT_ACCOUNT_MGMT_MODULE,
                externalPath: lightAccountManagementExternalPath,
            },
        },
        [ACCOUNT_MGMT_MODULE]: {
            id: ACCOUNT_MGMT_MODULE,
            groupId: ACCOUNT_MGMT_MODULE,
            hideNavItem: true,
            component: AccountManagement,
            href: accountManagementHrefGetter,
            legacy: true,
            isDefault: false,
            permissions: ACCOUNT_MGMT_PERMISSIONS,
            componentProps: {
                key: ACCOUNT_MGMT_MODULE,
                id: ACCOUNT_MGMT_MODULE,
                externalPath: accountManagementExternalPath,
            },
        },
        [USER_MGMT_MODULE]: {
            id: USER_MGMT_MODULE,
            groupId: USER_MGMT_MODULE,
            hideNavItem: true,
            component: UserManagement,
            href: userManagementHrefGetter,
            legacy: true,
            isDefault: false,
            permissions: USER_MGMT_PERMISSIONS,
            componentProps: {
                key: USER_MGMT_MODULE,
                id: USER_MGMT_MODULE,
                externalPath: userManagementExternalPath,
            },
        },
        [BILLING_AND_PAYMENTS_MODULE]: {
            id: BILLING_AND_PAYMENTS_MODULE,
            groupId: BILLING_AND_PAYMENTS_MODULE,
            hideNavItem: true,
            component: BillingAndPaymentsModulePage,
            isDefault: false,
            visibilityCondition: billingAndPaymentVisibilityCondition,
            caption: {
                id: 'navigation.caption.billing-and-payments',
                default: 'Billing & Payments',
            },
            componentProps: {
                id: BILLING_AND_PAYMENTS_MODULE,
            },
        },
        [CAMPAIGN_REVIEW_MODULE]: {
            id: CAMPAIGN_REVIEW_MODULE,
            groupId: CAMPAIGN_REVIEW_MODULE,
            hideNavItem: true,
            component: CampaignReviewManagement,
            isDefault: false,
            permissions: CAMPAIGN_REVIEW_PERMISSIONS,
            href: campaignReviewHrefGetter,
            legacy: true,
            componentProps: {
                key: CAMPAIGN_REVIEW_MODULE,
                id: CAMPAIGN_REVIEW_MODULE,
                externalPath: campaignReviewExternalPath,
            },
        },
        [CONTACT_SUPPORT_MODULE]: {
            id: CONTACT_SUPPORT_MODULE,
            groupId: CONTACT_SUPPORT_MODULE,
            hideNavItem: true,
            component: ContactSupport,
            href: contactSupportHrefGetter,
            legacy: true,
            isDefault: false,
            permissions: CONTACT_SUPPORT_PERMISSIONS,
            visibilityCondition: ({ selectedAccount: { tier }, accountConfig }) =>
                isLowTouchAccount(tier) || accountConfig?.[FEATURE_FLAGS.FEEDBACK_FORM_IS_VISIBLE] === 'true',
            accountConfigurations: { [FEATURE_FLAGS.CONTACT_SUPPORT_CAN_BE_VISIBLE]: 'true' },
            componentProps: {
                key: CONTACT_SUPPORT_MODULE,
                id: CONTACT_SUPPORT_MODULE,
                externalPath: contactSupportExternalPath,
            },
        },
        [SEARCH_ADD_KEYWORD_MODULE]: {
            id: SEARCH_ADD_KEYWORD_MODULE,
            component: SearchAdsKeywordComponent,
            permissions: SEARCH_ADD_KEYWORD_PERMISSIONS,
            accountConfigurations: { [FEATURE_FLAGS.ALLOW_SEARCH_ADS_REPORTS]: 'true' },
            componentProps: { id: SEARCH_ADD_KEYWORD_MODULE },
            caption: {
                id: 'navigation.caption.search-ads-keyword',
                default: 'Search Ads Keyword',
            },
        },
        [CAMPAIGN_BENCHMARKING_MODULE]: {
            id: CAMPAIGN_BENCHMARKING_MODULE,
            component: CampaignBenchmarkingComponent,
            permissions: CAMPAIGN_BENCHMARKING_PERMISSIONS,
            componentProps: { id: CAMPAIGN_BENCHMARKING_MODULE },
            hideNavItem: true,
            hidden: true,
            caption: {
                id: 'navigation.caption.campaign-benchmarking',
                default: 'Campaign Benchmarking',
            },
        },
        [ONBOARDING_MODULE]: {
            id: ONBOARDING_MODULE,
            component: Onboarding,
            hidden: true,
            hideHeader: true,
            permissions: ['CAMPAIGN_VIEW'],
            disableMultiLayerDrawer: true,
        },
        [CODELESS_MODULE]: {
            id: CODELESS_MODULE,
            component: ConversionSetupWrapper,
            hidden: true,
            hideHeader: true,
            permissions: ['CODELESS_EVENTS_DEVELOPER'],
        },
        [PUSH_API_MODULE]: {
            id: PUSH_API_MODULE,
            component: PushApi,
            icon: CodeIcon,
            hideNavItem: true,
            caption: {
                id: 'navigation.caption.push-api',
                default: 'API Integrations',
            },
            visibilityCondition: ({ permissions }) => permissions.PUSH_API_VIEW,
            permissions: ['PUSH_API_VIEW'],
        },
    },
    externalApps: {
        helpCenter: {
            id: 'app.externalApps.helpCenter',
            hrefId: 'app.externalApps.helpCenter.href',
            color: '#00CB84',
        },
        trends: {
            id: 'app.externalApps.trends',
            href: 'https://trends.taboola.com/',
            color: '#2A3F4F',
        },
        marketingPartners: {
            id: 'app.externalApps.marketingPartners',
            href: 'https://marketing-partners.taboola.com/',
            color: '#84BBF9',
        },
        backstage: {
            id: 'app.externalApps.backstage',
            hrefId: 'app.externalApps.backstage.href',
            color: '#2180C0',
            visibilityCondition: ({ accountConfig, permissionsMap }) => {
                const shouldCheckForPermissions =
                    accountConfig?.[FEATURE_FLAGS.ENABLE_EXTERNAL_APPS_PERMISSIONS] === 'true';
                if (!shouldCheckForPermissions) {
                    return true;
                }
                return permissionsMap['ENABLE_BACKSTAGE_FROM_ADS_CONSOLE'];
            },
        },
        offstage: {
            id: 'app.externalApps.offstage',
            href: 'https://offstage.taboola.com',
            color: '#CF7299',
            permissions: CAMPAIGN_REVIEW_PERMISSIONS,
        },
        salesforce: {
            id: 'app.externalApps.salesforce',
            href: 'https://taboola.lightning.force.com/lightning/page/home',
            color: '#45CED6',
            permissions: CAMPAIGN_REVIEW_PERMISSIONS,
        },
        searchKeywordTool: {
            id: 'app.externalApps.searchKeywordTool',
            href: 'https://search-ads-keyword.taboolasyndication.com',
            color: '#706AA6',
            permissions: CAMPAIGN_REVIEW_PERMISSIONS,
        },
        featureRequests: {
            id: 'app.externalApps.featureRequests',
            href: 'https://tbla.atlassian.net/jira/polaris/projects/TAP/ideas/view/2892811',
            color: '#4D86EC',
            permissions: CAMPAIGN_REVIEW_PERMISSIONS,
        },
        customerHub: {
            id: 'app.externalApps.customerHub',
            href: ({ selectedAccount: { successPlanUrl } }) => successPlanUrl,
            color: '#706AA6',
            visibilityCondition: ({ selectedAccount: { successPlanUrl } }) => {
                return successPlanUrl;
            },
        },
        campaignBenchmarking: {
            id: 'app.externalApps.campaignBenchmarking',
            href: '/campaign-benchmarking',
            color: '#f9b584',
            permissions: CAMPAIGN_BENCHMARKING_PERMISSIONS,
        },
    },
    legal: {
        privacy: {
            title: <FormattedMessage id="app.legal.privacy" />,
            hrefId: 'app.legal.privacy.href',
        },
        termOfUse: {
            title: <FormattedMessage id="app.legal.termOfUse" />,
            hrefId: 'app.legal.termOfUse.href',
        },
        cookiesConsent: {
            Component: CookieConsent,
            title: 'app.legal.cookieConsent',
            hrefId: 'app.legal.termOfUse.href',
        },
    },
    metrics: {
        options: {
            autoTrackPageView: false,
            useTrackBinding: false,
        },
        config: {
            vendors: [
                {
                    name: 'GTM Tracker',
                    api: {
                        track: gtmTracker.trackEvent,
                    },
                },
            ],
        },
    },
    analyticsEventCreators: {
        [FETCH_USER_SUCCESS]: action => ({
            event: GTM_EVENTS.USER,
            value: isInternalUser(action.payload) ? USER_TYPE.INTERNAL : USER_TYPE.EXTERNAL,
            userId: action.payload.user_id,
            accountId: action.payload.account_id,
        }),
        ...campaignsConfig.analyticsEventCreators,
    },
};

export default config;
